import classNames from 'classnames'
import * as React from 'react'
import type {SyntheticEvent} from 'react'

import {BS} from '../../../../types/BS_types'
import {emptyArray} from '../../../../utils/empty'
import Markdown from '../../../common/Markdown/Markdown'
import TruncateHtml from '../../../common/TruncateHtml/TruncateHtml'

import HealthItemActions from './HealthItem.actions'
import HealthItemDialog from './HealthItem.dialog'
import type {HealthItemOwnProps} from './HealthItem.types'
import {getSeverityClassName} from './HealthItem.utils'
import HealthItemIcon from './HealthItemIcon'

import styles from './HealthItem.css'

const LINE_HEIGHT = 16

declare global {
  interface Element {
    refresh(): void
  }
}

const refreshGlobalHealthItems = () => {
  const element = document.getElementById('globalHealthItems')

  if (element != null) {
    element.refresh()
  }
}

export const hideHealthItem = (item: HealthItemOwnProps, global: boolean) =>
  BS.AdminActions?.setHealthItemVisibility(
    item.extension.type,
    item.category.id,
    item.identity,
    false,
    global,
    refreshGlobalHealthItems,
  )

function HeaderHealthItem(props: HealthItemOwnProps) {
  const {
    canHidePersonal,
    canHideGlobal,
    renderBeforeHeader,
    identity,
    severity,
    category,
    grouped,
    inline,
    truncate = true,
    parentSelector = '',
  } = props
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [showPopup, setShowPopup] = React.useState(false)
  const [truncated, setTruncated] = React.useState<boolean | null>(null)
  // This element is an origin Health Item with all events on DOM elements
  const healthItemElement = React.useMemo(
    () => document.querySelector(`${parentSelector}[data-health-report-identity='${identity}']`),
    [identity, parentSelector],
  )
  const markdown = React.useMemo(
    () => healthItemElement?.querySelector('[data-health-report-markdown-message]')?.textContent,
    [healthItemElement],
  )
  // This placeholder will be attached to the Header when original Health Item moves to the popup
  const healthItemElementPlaceholder: HTMLElement | undefined = React.useMemo(
    () => healthItemElement?.cloneNode(true) as HTMLElement | undefined,
    [healthItemElement],
  )
  const disableTabNavigation = React.useCallback(() => {
    if (truncated !== true) {
      return
    }

    const inputElements = healthItemElement
      ? [...healthItemElement.querySelectorAll('button, a, select, input')]
      : emptyArray
    inputElements.forEach(item => {
      item.tabIndex = -1
    })
  }, [healthItemElement, truncated])
  const enableTabNavigation = React.useCallback(() => {
    const inputElements = healthItemElement
      ? [...healthItemElement.querySelectorAll('button, a, select, input')]
      : emptyArray
    inputElements.forEach(item => {
      item.tabIndex = 0
    })
  }, [healthItemElement])
  React.useEffect(() => {
    if (
      containerRef.current == null ||
      healthItemElement == null ||
      healthItemElementPlaceholder == null
    ) {
      return
    }

    healthItemElement.classList.remove('hidden')
    healthItemElementPlaceholder.classList.remove('hidden')
    containerRef.current.appendChild(showPopup ? healthItemElementPlaceholder : healthItemElement)
  }, [disableTabNavigation, healthItemElement, healthItemElementPlaceholder, showPopup])
  const onTruncateReady = React.useCallback(
    (truncateResult: boolean) => {
      if (truncateResult) {
        disableTabNavigation()
      }

      setTruncated(truncateResult)
    },
    [disableTabNavigation],
  )
  const onHidePersonal = React.useCallback(() => {
    hideHealthItem(props, false)
  }, [props])
  const onHideGlobal = React.useCallback(() => {
    hideHealthItem(props, true)
  }, [props])
  const onShow = React.useCallback(() => {
    enableTabNavigation()
    setShowPopup(true)
  }, [enableTabNavigation])
  const onHide = React.useCallback(() => {
    disableTabNavigation()
    setShowPopup(false)
  }, [disableTabNavigation])
  const onContentClick = React.useCallback(
    (event: SyntheticEvent) => {
      const isLink = (event.target as Element).tagName.toLowerCase() === 'a'

      if (!isLink) {
        onShow()
      }
    },
    [onShow],
  )

  // We don't trancate markdown for the first iteration.
  const content = markdown ? (
    <Markdown
      className={classNames(styles.markdown, styles.content, {
        [styles.grouped]: grouped,
        [styles.inline]: inline,
      })}
    >
      {markdown}
    </Markdown>
  ) : (
    <div ref={containerRef} />
  )

  const container = (
    <div
      className={classNames(styles.container, {
        [styles.groupedContent]: grouped,
      })}
    >
      {!inline && !grouped && <HealthItemIcon className={styles.icon} severity={severity} />}
      {truncate && markdown == null ? (
        <TruncateHtml
          toggleButtonClass={styles.toggleButton}
          className={classNames(styles.content, {
            [styles.grouped]: grouped,
            [styles.inline]: inline,
          })}
          onReady={onTruncateReady}
          withToggleButton={false}
          clamp={1}
          lineHeight={LINE_HEIGHT}
          maxHeight={markdown ? 'max-content' : undefined}
        >
          {truncated === true ? (
            /* This container is not reached using a Keyboard if truncated. Instead, the user will be
             * pointed to the "Show All" button, which has a description and then will be trapped in the Popup */
            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
            <div
              className={styles.truncatedContainer}
              onClick={onContentClick}
              role={'button'}
              tabIndex={-1}
            >
              {content}
            </div>
          ) : (
            content
          )}
        </TruncateHtml>
      ) : (
        content
      )}
      {!inline && (
        <HealthItemActions
          grouped={grouped}
          hidePersonal={canHidePersonal ? onHidePersonal : undefined}
          hideGlobal={canHideGlobal ? onHideGlobal : undefined}
          showMore={truncated === true ? onShow : null}
          category={category}
        />
      )}
    </div>
  )

  return (
    <>
      {inline || grouped ? (
        container
      ) : (
        <div
          className={classNames(styles.outerContainer, getSeverityClassName(severity), {
            [styles.renderBeforeHeader]: renderBeforeHeader === 'true',
          })}
        >
          {container}
        </div>
      )}
      <HealthItemDialog
        healthItemElement={healthItemElement}
        show={showPopup}
        close={onHide}
        hidePersonal={canHidePersonal ? onHidePersonal : undefined}
        hideGlobal={canHideGlobal ? onHideGlobal : undefined}
        identity={identity}
        category={category}
        markdown={markdown}
      />
    </>
  )
}

export default HeaderHealthItem
